// @flow
import React from 'react';
import styled from 'styled-components';
import { imageUrl } from 'utils/filestack';
import Box from 'components/Box';
import Parallax, { PARALLAX_DEFAULT_SPEED } from 'containers/Parallax';
import {
  RichText,
  Body,
  BodyLarge,
  BodySmall,
  HeadingSmall
} from 'components/Text';
import ShowModalContainer from 'containers/ShowModalContainer';
import ButtonLink from 'components/Cms/ButtonLink';
import Button from 'components/Button';
import Video, { PlayButton } from 'components/Video';
import Img, { FilestackImage, getSrc } from 'components/Image';
import type { SplitContentBlockData, SplitContentSection } from 'api/cms';
import { hiDPI } from 'utils/style';
import { colors, gutterWidth, bp } from '../../theme';

const { useEffect, useState } = React;

const Blockquote = styled(Box)`
  &::before {
    content: '\\201c';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -35%);
    color: ${colors.dandelion};
    font-size: 100px;
    font-weight: 700;
    line-height: 1;
  }
`;

const ImageContainer = styled(
  ({ handle, isGif, isJpg, fullWidth, overlapping, ...props }) => (
    <Box {...props} />
  )
)`
  width: 100%;
  height: 100%;
  background-position: center;
  ${props => `
    background-size: ${props.isGif ? 'contain' : 'cover'};
    background-image: url('${getSrc({
      handle: props.handle,
      width: props.fullWidth ? 996 : props.overlapping ? 696 : 552,
      format: !(props.isGif || props.isJpg) ? 'jpg' : undefined
    })}');
    ${hiDPI()} {
      background-image: url('${getSrc({
        handle: props.handle,
        width: props.fullWidth ? 1992 : props.overlapping ? 1392 : 1104,
        format: !(props.isGif || props.isJpg) ? 'jpg' : undefined
      })}');
    }
    ${
      !props.isGif
        ? `
      * {
        width: 100%;
        height: 100%;
      }
      svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 40px;
        height: 40px;
      }
      img {
        width: 100%;
        height: auto;
        display: none;
      }
      @supports (object-fit: cover) {
        background-image: none;
        img {
          height: 100%;
          object-fit: cover;
          display: block;
        }
      }
    `
        : `
      img {
        width: 100%;
      }
        `
    }
  `}
`;

const GifImage = styled(Img)`
  max-height: 500px;
  max-width: 100%;
  width: auto;
  @supports (object-fit: contain) {
    object-fit: contain;
    width: 100%;
    height: 100%;
    max-height: none;
  }
`;

const Content = ({
  section,
  fullWidth,
  overlapping,
  isGif,
  isJpg,
  showTextBox,
  button,
  blockIndex,
  pageUpdated
}: {
  section: SplitContentSection,
  fullWidth: boolean,
  overlapping: boolean,
  isGif: boolean,
  isJpg: boolean,
  showTextBox: boolean,
  button?: { title: string, href: string },
  blockIndex: number,
  pageUpdated: string
}) => {
  switch (section.type) {
    case 'TEXT': {
      return (
        <Box
          position="relative"
          p={[gutterWidth / 2, null, gutterWidth]}
          pr={showTextBox || [null, null, 0]}
          height="100%"
        >
          {section.data.label && (
            <HeadingSmall color={colors.battleshipGrey}>
              {section.data.label}
            </HeadingSmall>
          )}
          {section.data.headline && (
            <Box
              display="block"
              mb={24}
              color={colors.greyishBrown}
              textDecoration="none"
            >
              <RichText
                dangerouslySetInnerHTML={{
                  __html:
                    section.data.headline.indexOf('h2') > -1
                      ? section.data.headline
                      : `<h2>${
                          section.data.headline
                            ? section.data.headline.replace(
                                /<(\/?)p>/g,
                                '<$1span>'
                              )
                            : ''
                        }</h2>`
                }}
              />
            </Box>
          )}
          <Body dangerouslySetInnerHTML={{ __html: section.data.html }} />
          {section.data.button && (
            <ButtonLink
              sectionButton={section.data.button}
              eventProperties={{
                block_position: blockIndex,
                block_type: 'Split Content',
                cta_type: 'Button',
                button_text: section.data.button.text,
                page_updated: pageUpdated
              }}
            />
          )}
        </Box>
      );
    }
    case 'BLOCKQUOTE':
      return (
        <Blockquote
          component="blockquote"
          position="relative"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          height="100%"
          m={0}
          p={[gutterWidth / 2, null, gutterWidth]}
        >
          <BodyLarge dangerouslySetInnerHTML={{ __html: section.data.quote }} />
          {section.data.cite && <BodySmall>{section.data.cite}</BodySmall>}
        </Blockquote>
      );
    case 'VIDEO':
      return (
        <ShowModalContainer
          render={showModal => (
            <Box
              component={Button}
              position="relative"
              width="100%"
              height="100%"
              onClick={() => {
                showModal('MEDIA_GALLERY', {
                  items: [{ type: 'VIDEO', videoUrl: section.data.url }],
                  itemIndex: 0
                });
              }}
              aria-label={getText('Open video')}
            >
              <Video
                minHeight={['50vw', null, '25vw', (540 * 9) / 16]}
                url={section.data.url}
                prefix="split-block-video-"
                fill
                noPlayer
                hideButton={fullWidth}
              />
            </Box>
          )}
        />
      );
    case 'IMAGE':
      return (
        <ImageContainer
          handle={section.data.image && section.data.image.handle}
          isGif={isGif}
          isJpg={isJpg}
          component={button ? 'a' : 'div'}
          href={button ? button.href : null}
          fullWidth={fullWidth}
          overlapping={overlapping}
        >
          {isGif ? (
            <GifImage src={imageUrl({ handle: section.data.image.handle })} />
          ) : (
            <FilestackImage
              data={section.data.image}
              alt={section.data.image && section.data.image.filename}
              width={fullWidth ? 1440 : overlapping ? 696 : 552}
              format="jpg"
            />
          )}
        </ImageContainer>
      );

    default:
      return null;
  }
};

const SplitContentBlock = ({
  data: { sections, appearance },
  fullWidthLayout,
  parallax,
  blockIndex,
  pageUpdated,
  preview
}: {
  data: SplitContentBlockData,
  fullWidthLayout: boolean,
  parallax: boolean,
  blockIndex: number,
  pageUpdated: string,
  preview?: boolean
}) => {
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const resizeHandler = () =>
      setIsDesktop(window && window.innerWidth >= bp[1]);
    window.addEventListener('resize', resizeHandler);
    resizeHandler();
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  const actualAppearance =
    fullWidthLayout &&
    sections.some(
      section => section.type === 'IMAGE' || section.type === 'VIDEO'
    ) &&
    sections.some(
      section => section.type === 'TEXT' || section.type === 'BLOCKQUOTE'
    )
      ? appearance
      : 'SIDE_BY_SIDE';

  const flipOnMobile =
    ['TEXT', 'BLOCKQUOTE'].includes(sections[0].type) &&
    ['IMAGE', 'VIDEO'].includes(sections[1].type);

  return (
    <Box py={actualAppearance !== 'SIDE_BY_SIDE' && gutterWidth}>
      <Box
        position="relative"
        display="flex"
        flexDirection={[
          flipOnMobile ? 'column-reverse' : 'column',
          null,
          'row'
        ]}
        alignItems={actualAppearance === 'FULL_WIDTH' && 'center'}
        m={[-gutterWidth / 4, null, -gutterWidth / 2]}
        width={[
          `calc(100% + ${gutterWidth / 2}px)`,
          null,
          `calc(100% + ${gutterWidth}px)`
        ]}
        minHeight={actualAppearance === 'FULL_WIDTH' && 'calc(100vh - 300px)'}
      >
        {sections.map((section, index) => {
          const isGif =
            section.type === 'IMAGE' &&
            section.data.image &&
            section.data.image.mimetype === 'image/gif';
          const isJpg =
            section.type === 'IMAGE' &&
            section.data.image &&
            section.data.image.mimetype === 'image/jpeg';
          const overlapping =
            actualAppearance === 'OVERLAPPING' &&
            (section.type === 'IMAGE' || section.type === 'VIDEO');
          const fullWidth =
            actualAppearance === 'FULL_WIDTH' &&
            (section.type === 'IMAGE' || section.type === 'VIDEO') &&
            isDesktop;
          const showTextBox =
            ['FULL_WIDTH', 'OVERLAPPING'].includes(actualAppearance) &&
            ['TEXT', 'BLOCKQUOTE'].includes(section.type);
          const withParallax = parallax && fullWidth;
          return (
            <Box
              position={!fullWidth && 'relative'}
              zIndex={showTextBox && 1}
              width={[1, null, 1 / 2]}
              p={[gutterWidth / 4, null, gutterWidth / 2]}
              // eslint-disable-next-line react/no-array-index-key
              key={`${section.type}-${index}`}
            >
              <Box
                position={[null, null, fullWidth && !preview && 'absolute']}
                left={[null, null, '50%']}
                top={[null, null, '50%']}
                transform={[
                  null,
                  null,
                  fullWidth && !preview && 'translate(-50%, -50%)'
                ]}
                height={[
                  '100%',
                  null,
                  overlapping || fullWidth
                    ? `calc(100% + ${gutterWidth * 2}px)`
                    : '100%'
                ]}
                mt={overlapping && [0, null, -gutterWidth]}
                mb={overlapping && [-gutterWidth, null, 0]}
                width={
                  overlapping
                    ? [1, null, `calc(100% + ${gutterWidth * 3}px)`]
                    : fullWidth
                    ? [1, null, '100vw']
                    : 1
                }
                ml={[0, null, overlapping && index === 1 && -gutterWidth * 3]}
                overflow={
                  ['TEXT', 'BLOCKQUOTE'].includes(section.type) || isGif
                    ? undefined
                    : 'hidden'
                }
                bg={
                  section.type === 'TEXT' || isGif || showTextBox
                    ? 'white'
                    : colors.fog
                }
                boxShadow={showTextBox && '0 0 6px rgba(0,0,0,0.3)'}
                borderRadius={[
                  showTextBox ? 12 : isGif || 24,
                  null,
                  fullWidth && 0
                ]}
                style={
                  section.type === 'IMAGE' && {
                    '-webkit-mask-image':
                      '-webkit-radial-gradient(white, black)'
                  }
                }
              >
                <>
                  <Parallax
                    disabled={!withParallax}
                    position={fullWidth && 'absolute'}
                    bottom={0}
                    left={0}
                    width="100%"
                    height={
                      withParallax
                        ? `calc(100% + ${PARALLAX_DEFAULT_SPEED}px)`
                        : '100%'
                    }
                  >
                    <Content
                      section={section}
                      isGif={isGif}
                      isJpg={isJpg}
                      showTextBox={showTextBox}
                      fullWidth={fullWidth}
                      overlapping={actualAppearance === 'OVERLAPPING'}
                      index={index}
                      parallax={parallax}
                      blockIndex={blockIndex}
                      pageUpdated={pageUpdated}
                    />
                  </Parallax>
                  {fullWidth && section.type === 'VIDEO' && (
                    <Box
                      position="absolute"
                      top="50%"
                      left={['50%', null, index === 0 ? '25%' : '75%']}
                      transform="translate(-50%, -50%)"
                      pointerEvents="none"
                    >
                      <PlayButton />
                    </Box>
                  )}
                </>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SplitContentBlock;
